<template>
  <div class="mock-management-panel">
    <b-form-group label="Источник данных чипов">
      <b-form-radio-group v-model="useMock" stacked @change="onToggle">
        <b-form-radio :value="false">Реальный API (bc70a.ru)</b-form-radio>
        <b-form-radio :value="true">Мок из БД</b-form-radio>
      </b-form-radio-group>
    </b-form-group>

    <hr />

    <h6 class="mb-2">Сохранённые моки (external_start_id)</h6>
    <ul v-if="mockList.length" class="list-unstyled mb-3">
      <li v-for="item in mockList" :key="item.externalStartId" class="mb-1">
        <code>{{ item.externalStartId }}</code>
      </li>
    </ul>
    <p v-else class="text-muted small">Нет сохранённых моков.</p>

    <template v-if="startId">
      <hr />
      <h6 class="mb-2">Мок для этого старта (ID {{ startId }})</h6>
      <b-form-group label="JSON (массив ChipReading)" label-for="mock-json">
        <b-form-textarea
          id="mock-json"
          v-model="jsonText"
          rows="12"
          placeholder='[{"epc":"","code":"101","num":"1","point":"Start","timestamp":"2025-12-21T00:00:00.000Z","antena":"1","rssi":"-60"}, ...]'
          class="font-monospace"
        />
        <small class="text-muted">Формат: массив объектов с полями epc, code, num, point, timestamp, antena, rssi.</small>
      </b-form-group>
      <div class="mock-actions mt-3 pt-2">
        <b-button variant="primary" size="sm" class="mr-2" :disabled="saving || !jsonValid" @click="saveMock">
          <b-spinner v-if="saving" small class="mr-1" />
          Сохранить мок
        </b-button>
        <b-button variant="outline-danger" size="sm" class="mr-2" :disabled="deleting || !hasMock" @click="deleteMock">
          <b-spinner v-if="deleting" small class="mr-1" />
          Удалить мок
        </b-button>
        <b-button variant="outline-secondary" size="sm" :disabled="loading" @click="loadMock">
          Загрузить текущий мок
        </b-button>
      </div>
      <b-alert v-if="jsonError" variant="danger" show class="mt-2 mb-0">{{ jsonError }}</b-alert>
    </template>
  </div>
</template>

<script>
import { BFormGroup, BFormRadioGroup, BFormRadio, BFormTextarea, BButton, BAlert, BSpinner } from 'bootstrap-vue'
import RaceAnalysisApiService from '@/services/race-analysis-api.service'

export default {
  name: 'MockManagementPanel',
  components: {
    BFormGroup,
    BFormRadioGroup,
    BFormRadio,
    BFormTextarea,
    BButton,
    BAlert,
    BSpinner,
  },
  props: {
    startId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      useMock: false,
      mockList: [],
      jsonText: '',
      loading: false,
      saving: false,
      deleting: false,
      hasMock: false,
    }
  },
  computed: {
    jsonValid() {
      if (!this.jsonText.trim()) return false
      try {
        const arr = JSON.parse(this.jsonText)
        return Array.isArray(arr)
      } catch {
        return false
      }
    },
    jsonError() {
      if (!this.jsonText.trim()) return ''
      try {
        JSON.parse(this.jsonText)
        return ''
      } catch (e) {
        return e.message || 'Некорректный JSON'
      }
    },
  },
  mounted() {
    this.loadSettings()
    this.loadMockList()
    if (this.startId) this.loadMock()
  },
  methods: {
    async loadSettings() {
      try {
        const res = await RaceAnalysisApiService.getMockSettings()
        this.useMock = res.useMock === true
      } catch (e) {
        this.$emit('error', e?.message || 'Не удалось загрузить настройки мока')
      }
    },
    async onToggle() {
      try {
        await RaceAnalysisApiService.setMockSettings(this.useMock)
      } catch (e) {
        this.$emit('error', e?.message || 'Не удалось сохранить настройки мока')
      }
    },
    async loadMockList() {
      try {
        const res = await RaceAnalysisApiService.getMockList()
        this.mockList = res.items || []
      } catch (e) {
        this.$emit('error', e?.message || 'Не удалось загрузить список моков')
      }
    },
    async loadMock() {
      if (!this.startId) return
      this.loading = true
      try {
        const res = await RaceAnalysisApiService.getMock(this.startId)
        this.jsonText = JSON.stringify(res.data || [], null, 2)
        this.hasMock = true
      } catch (e) {
        if (e?.response?.status === 404) {
          this.jsonText = '[]'
          this.hasMock = false
        } else {
          this.$emit('error', e?.message || 'Не удалось загрузить мок')
        }
      } finally {
        this.loading = false
      }
    },
    async saveMock() {
      if (!this.startId || !this.jsonValid) return
      this.saving = true
      try {
        const data = JSON.parse(this.jsonText)
        await RaceAnalysisApiService.putMock(this.startId, data)
        this.hasMock = true
        await this.loadMockList()
        this.$emit('saved')
      } catch (e) {
        this.$emit('error', e?.message || 'Не удалось сохранить мок')
      } finally {
        this.saving = false
      }
    },
    async deleteMock() {
      if (!this.startId) return
      this.deleting = true
      try {
        await RaceAnalysisApiService.deleteMock(this.startId)
        this.jsonText = '[]'
        this.hasMock = false
        await this.loadMockList()
        this.$emit('deleted')
      } catch (e) {
        this.$emit('error', e?.message || 'Не удалось удалить мок')
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.mock-management-panel .font-monospace {
  font-size: 0.85rem;
}

.mock-actions {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}
.mock-actions .btn {
  min-width: 8rem;
}
</style>
